/**
 * /zh-tw/prd — PRD (產品需求文件) concept page (Traditional Chinese).
 * Links /zh-tw/product-roadmap + /zh-tw/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwPrd() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/prd"
      hreflangLocales={['zhtw']}
      documentTitle="PRD 是什麼？產品需求文件寫法與範本 · Draftlize"
      documentDescription="產品需求文件（PRD）的意義、結構與範本一次說清楚，並教你打造不會每個 sprint 就過期的「活的 PRD」。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            產品需求文件（PRD）<br />
            <em>是什麼。</em>
          </h1>
          <p className="lede">
            <strong>PRD</strong>（Product Requirements Document／產品需求文件）定義<em>要做什麼</em>以及<em>為什麼</em>：問題、範圍、需求，以及背後的決策，讓團隊和關係人有同一張圖。弱點在後面——過了兩三個 sprint，PRD 就和現實脫節，開始<em>漂移</em>。這頁講結構與範本，以及怎麼防止漂移。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">撐得住的 PRD 結構。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>問題與目標</strong>——誰的問題、為何是現在、要達成什麼成果。</li>
            <li><strong>範圍（做／不做）</strong>——刻意不包含的部分。</li>
            <li><strong>需求</strong>——功能與非功能，附優先順序。</li>
            <li><strong>決策與取捨</strong>——考慮過什麼、為何捨棄其他。</li>
            <li><strong>待解問題與成功指標</strong>。</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            這個範本可以直接套用。最有價值、也最快過期的是第 4 項——決策。PRD 只是 <a href="/zh-tw/product-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>路線圖</a> 和底下決策的「看得見的尖端」。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">真正的問題是 <em>PRD 漂移。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            PRD 在核准當天是對的。之後，一場會議改了假設，一個邊界案例推翻了決策——文件卻停在原地。沒人會手動去追一份 12 頁的文件。幾週後，AI agent 開始在一份已經不成立的規格上動工。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">需求變成卡片</h3>
            <p>每條需求與決策，變成可引用的卡片，而不是第 7 頁的一行字——帶著理由與負責人，用 ID 就找得到。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">決策一變，PRD 就提醒</h3>
            <p>後面的決策推翻前面的，依賴的卡片——也就是受影響的 PRD 部分——自動標 stale。文件不會再默默騙你三週。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 讀到最新的規格</h3>
            <p>Claude Code、Cursor 經 MCP 讀 PRD——是最新的，或被明確標為 stale，而不是一份沒人知道狀態的靜態文件。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          PRD 在核准當天是對的。問題是核准之後那六週，沒人讓它跟上。
        </blockquote>
        <cite>把需求變成卡片，PRD 自己就會說「我過期了」。這就是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PRD 和規格書差在哪？', a: 'PRD 定義「做什麼、為什麼」，屬於上層；規格書處理「怎麼做」。PRD 的決策一變，規格也該變，這個連動很關鍵。' },
            { q: '誰負責寫？', a: '多半由 PM 主筆，和工程、設計協作。重點是讓裡面的決策日後追得回來、理由留得住。' },
            { q: '要寫多長？', a: '把問題、範圍、需求、決策講清楚就夠了。比起長度，「被推翻時能被提醒」更重要。' },
            { q: '怎麼避免過期？', a: '把需求和決策做成互相連結的卡片，而不是凍住的段落。決策一變，Draftlize 自動把相關處標 stale。延伸讀 <link>。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>
                {f.a.replace('延伸讀 <link>。','')}
                {i === 3 && (<>{'延伸讀 '}<a href="/zh-tw/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄</a>{'。'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">一份不會漂移的 PRD<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用連結的卡片寫 PRD，每條需求都能被引用。決策一變就自動把相關處標 stale——PRD 漂移就此結束。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
